import { CalendarCheck, Heart, MapPin, MessageCircle, PawPrint, Scissors, Sparkles, Star } from 'lucide-react';
import { formatCents } from '@smup/availability';
import '@fontsource-variable/bodoni-moda';
import '@fontsource-variable/montserrat';
import '@fontsource/allison';
import type { Site } from './BookingPage';
import { Photo, Social, scrollToId, type TemplateProps } from './LandingTemplates';
/* Her logo lockup, cut from her business card with the marble keyed out to transparency. Her
   own artwork, not a re-set: an earlier version typeset it and drew the scissors, and it
   didn't match. 696px wide, so it softens slightly past ~350px on a 2x screen. */
import logo from './assets/show-shears-logo.png';
import { BrickGallery } from './BrickGallery';

/*
 * ⚠️ A ONE-OFF PAGE FOR SHOW SHEARS GROOMING (Juliana Palmer, Pendleton SC). Same bargain as
 * Fluffy Paw Spa's: bespoke paid work, chosen by tenant slug in App.tsx, never a template a
 * founder could hand to somebody else. The look is taken from her business card — black
 * marble, gold Didone lettering, spaced capitals, a script sign-off.
 *
 * ⚠️ COLOURS STILL COME FROM `site.branding`. Black is her background and gold her accent,
 * both stored on the tenant, because the booking flow under this page paints from the same
 * two values and the seam must not show. The only hardcoded tones are the highlight and
 * shadow of the gold gradient, which are mixes of the accent rather than new colours.
 *
 * ⚠️ THE FONTS ARE SELF-HOSTED, and must stay that way. Tenant domains are served with
 * `font-src 'self'` (infra/Caddyfile, csp_spa), so a Google Fonts link would be blocked
 * silently and the page would fall back to Times. @font-face is lazy: other tenants' pages
 * carry the declarations in the shared CSS but never download a file.
 *
 * ⚠️ NO PLACEHOLDERS ON THIS ONE. Sections with nothing to show (no services yet, no add-ons,
 * no quotes) are simply absent, unlike Fluffy Paw's dashed "review goes here" cards — a gap
 * reads as unfinished on a page this dressed up.
 */

const SERIF = "'Bodoni Moda Variable', 'Didot', 'Bodoni 72', Georgia, serif";
const SANS = "'Montserrat Variable', ui-sans-serif, system-ui, sans-serif";
const SCRIPT = "'Allison', 'Snell Roundhand', cursive";

/*
 * Faint white veining over the black, so the background reads as stone rather than flat
 * #000 — the card's marble. A data: URI because `img-src` allows `data:` and nothing else
 * external. Turbulence's thin low-value ridges are the veins; the alpha row of the matrix
 * keeps only those and caps them at a few percent.
 *
 * ⚠️ THE FREQUENCIES ARE 4/900 AND 6/900 ON PURPOSE. `stitchTiles` only joins cleanly when
 * frequency × tile size is a whole number; 0.0045 left a hard horizontal seam every 900px.
 */
const MARBLE = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='900' height='900'><filter id='v'><feTurbulence type='turbulence' baseFrequency='0.0044444 0.0066667' numOctaves='4' seed='23' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 -1.1 0 0 0 0.09'/></filter><rect width='100%' height='100%' filter='url(#v)'/></svg>")}")`;

/** The card's four promises, in its order and its words. */
const PILLARS = [
  [Heart, 'One-on-one care'],
  [PawPrint, 'All breeds welcome'],
  [Sparkles, 'Low stress environment'],
  [Scissors, 'Expert grooming'],
] as const;

/* Where she works, from her Facebook page. There is no field for this, so it lives here like
   the card's tagline does; change it here if she moves. */
const PLACES = [
  ['Pendleton', 'My home salon', 'Completely one on one. No other dogs, and no crating unless you ask for it.'],
  ['Starr', 'A boarding & grooming facility', "A regular salon setting with other dogs around, but I'm still your dog's only groomer, start to finish."],
  ['Your place', 'House calls', "For homes with several dogs. I'll need water and good light; I bring everything else. Travel adds a fee."],
] as const;

/*
 * The circle above "Welcome" (Mike, 2026-10-26): gallery photo 20, her lying in the grass with
 * a Frenchie. Picked by ID, not position, so reordering the gallery can't swap it for another
 * photo; if it's deleted the circle just goes away. The focus is set here rather than on the
 * photo because the gallery shows the whole picture; only this circle needs it low, where both
 * faces are.
 */
const WELCOME_PHOTO_ID = '9caec0a8-a217-409f-a4b2-c57bd7c5c6bf';
const WELCOME_PHOTO_FOCUS = { focalX: 50, focalY: 85 };

export function LandingOneOffShowShears({ site, landing, onPickService }: TemplateProps) {
  const { accentColor: gold, backgroundColor: ink, textColor } = site.branding;
  const hero = landing.photos[0];
  const more = landing.photos.slice(1);
  const welcomePhoto = landing.photos.find((p) => p.id === WELCOME_PHOTO_ID);

  /*
   * ⚠️ INK ON GOLD, NOT THE `onAccent` WE ARE HANDED. App.tsx picks white for anything under
   * 0.5 luminance, and her gold is about 0.45 — white on it is roughly 2:1, which fails. The
   * booking flow's own rule (0.35) already prints ink on this gold, so buttons here match it.
   */
  const onGold = ink;
  const goldLight = `color-mix(in srgb, ${gold} 55%, #FFF4DC)`;
  const goldDark = `color-mix(in srgb, ${gold} 70%, #3A2410)`;
  const metal = `linear-gradient(100deg, ${goldDark} 0%, ${gold} 28%, ${goldLight} 50%, ${gold} 72%, ${goldDark} 100%)`;
  const rule = `color-mix(in srgb, ${gold} 55%, transparent)`;
  const faint = `color-mix(in srgb, ${gold} 22%, transparent)`;
  const panel = `color-mix(in srgb, ${textColor} 4%, ${ink})`;

  const goldText: React.CSSProperties = {
    backgroundImage: metal,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  const fromCents = (s: Site['services'][number]) =>
    s.tiered && s.tiers.length ? Math.min(...s.tiers.map((t) => t.priceCents)) : s.priceCents;
  const sizeRange = (s: Site['services'][number]) => {
    const heads = s.tiers.map((t) => t.label.split(' ')[0]);
    if (heads.length === 0) return null;
    return heads.length <= 3 ? heads.join(' · ') : `${heads[0]} through ${heads[heads.length - 1]}`;
  };
  const duration = (mins: number) => {
    if (mins < 60) return `About ${mins} minutes`;
    const halves = Math.round(mins / 30) / 2;
    const whole = Math.floor(halves);
    return `About ${whole}${halves - whole ? '½' : ''} hour${halves === 1 ? '' : 's'}`;
  };

  const addons = Array.from(new Map(site.services.flatMap((s) => s.addons).map((a) => [a.id, a])).values());
  const addonPrice = (a: (typeof addons)[number]) =>
    a.priceUnit === 'minute'
      ? `${formatCents(a.priceCents)}–${formatCents(a.priceMaxCents ?? a.priceCents)} / min`
      : a.priceMaxCents && a.priceMaxCents !== a.priceCents
        ? `${formatCents(a.priceCents)}–${formatCents(a.priceMaxCents)}`
        : formatCents(a.priceCents);

  const repeats = site.settings.recurringOptions.filter((r) => r.percentOff > 0);
  const everyN: Record<string, string> = {
    weekly: 'every week',
    biweekly: 'every 2 weeks',
    every_4_weeks: 'every 4 weeks',
    every_6_weeks: 'every 6 weeks',
    monthly: 'every month',
  };

  /* The card says "Contact me via Facebook Messenger" — so a Facebook link, when she has
     saved one, is the contact line. No phone: the number printed on the card is a mock-up. */
  const facebook = landing.social.find((l) => l.platform === 'facebook');

  const nav = [
    site.services.length > 0 && (['Services', 'services'] as const),
    (['Our promise', 'promise'] as const),
    (['Where', 'where'] as const),
    more.length > 0 && (['Gallery', 'gallery'] as const),
    addons.length > 0 && (['Add-ons', 'extras'] as const),
  ].filter(Boolean) as (readonly [string, string])[];

  /** Spaced capitals with a gold rule either side — "—— G R O O M I N G ——" on the card. */
  const Ruled = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
    <div className={`flex items-center gap-4 ${className}`}>
      <span className="h-px flex-1" style={{ backgroundColor: rule }} />
      {children}
      <span className="h-px flex-1" style={{ backgroundColor: rule }} />
    </div>
  );

  const Eyebrow = ({ children }: { children: React.ReactNode }) => (
    <Ruled className="mx-auto max-w-md">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.32em]" style={{ color: gold }}>
        {children}
      </p>
    </Ruled>
  );

  const GoldButton = ({ onClick, children, outline = false, small = false }: {
    onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    children: React.ReactNode;
    outline?: boolean;
    small?: boolean;
  }) => (
    <a
      href="#book"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-2 rounded-full font-semibold uppercase tracking-[0.18em] transition-transform hover:scale-[1.03] motion-reduce:transition-none motion-reduce:hover:scale-100 ${
        small ? 'px-4 py-2 text-[0.68rem]' : 'px-7 py-3.5 text-xs'
      }`}
      style={
        outline
          ? { border: `1px solid ${gold}`, color: gold }
          : { backgroundImage: metal, color: onGold, boxShadow: `0 8px 30px -12px ${gold}` }
      }
    >
      {children}
    </a>
  );

  const toBook = (e: React.MouseEvent) => scrollToId(e, 'book');

  return (
    <div
      style={{
        fontFamily: SANS,
        backgroundColor: ink,
        backgroundImage: `radial-gradient(ellipse at 15% 0%, color-mix(in srgb, ${textColor} 6%, transparent), transparent 55%), ${MARBLE}`,
      }}
    >
      {/* Masthead. Sticky so Book is always one tap away; the full lockup is in the hero. */}
      <header
        className="sticky top-0 z-20 backdrop-blur"
        style={{ backgroundColor: `color-mix(in srgb, ${ink} 82%, transparent)`, borderBottom: `1px solid ${faint}` }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3.5">
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
            }}
            className="text-lg leading-none tracking-[0.08em] sm:text-xl"
            style={{ fontFamily: SERIF }}
          >
            SHOW <span style={goldText}>SHEARS</span>
          </a>
          <nav className="hidden gap-8 text-[0.7rem] font-semibold uppercase tracking-[0.22em] opacity-80 wide-md:flex">
            {nav.map(([label, id]) => (
              <a key={id} href={`#${id}`} onClick={(e) => scrollToId(e, id)} className="hover:opacity-100">
                {label}
              </a>
            ))}
          </nav>
          <GoldButton onClick={toBook} small>
            <CalendarCheck className="h-3.5 w-3.5" />
            Book
          </GoldButton>
        </div>
      </header>

      {/* Hero: the card's lockup on the left, her photo in a gold ring on the right. */}
      <section id="top" className="mx-auto max-w-6xl px-5 pb-16 pt-12 sm:pt-16">
        <div className="grid items-center gap-12 wide-md:grid-cols-[1.05fr_0.95fr]">
          <div className="text-center">
            {/*
              ⚠️ THE H1 IS HER NAME, SET AS THE CARD SETS IT, and the words are real text so a
              search engine and a screen reader both read "Show Shears Grooming". The scissors
              standing in for the A is decorative: the real letter is there, visually hidden.
            */}
            <h1>
              <img src={logo} alt="Show Shears Grooming" width={696} height={327} className="mx-auto h-auto w-full max-w-[34rem]" />
            </h1>
            {landing.tagline && (
              <p className="mt-7 text-[0.7rem] font-medium uppercase tracking-[0.3em] opacity-85 sm:text-sm">
                {landing.tagline}
              </p>
            )}
            <div className="mx-auto mt-7 h-px max-w-sm" style={{ backgroundColor: rule }} />
            <div className="mt-8 flex flex-col items-center gap-5">
              <GoldButton onClick={toBook}>
                <CalendarCheck className="h-4 w-4" />
                Book an appointment
              </GoldButton>
              <p className="text-4xl sm:text-5xl" style={{ fontFamily: SCRIPT, color: gold }}>
                Because they deserve the best.
              </p>
            </div>
          </div>

          {/*
            Her photo in a circle with two gold rings, the card's arc. Without a photo the
            rings frame the scissors mark instead, so the hero still balances.
          */}
          <div className="relative mx-auto aspect-square w-full max-w-[26rem]">
            <div className="absolute -inset-3 rounded-full" style={{ border: `1px solid ${faint}` }} />
            <div className="absolute inset-0 overflow-hidden rounded-full" style={{ boxShadow: `0 0 0 2px ${gold}, 0 30px 80px -30px ${gold}` }}>
              {hero ? (
                <Photo photo={hero} className="h-full w-full" />
              ) : (
                <div className="grid h-full w-full place-items-center" style={{ backgroundColor: panel }}>
                  <Scissors aria-hidden="true" strokeWidth={0.8} className="h-1/3 w-1/3 -rotate-90" style={{ color: gold }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* The four promises, as the card lays them out: gold line icons between gold rules. */}
      <section id="promise" className="scroll-mt-16" style={{ borderTop: `1px solid ${faint}`, borderBottom: `1px solid ${faint}` }}>
        <ul className="mx-auto grid max-w-6xl grid-cols-2 px-5 py-10 wide-md:grid-cols-4">
          {PILLARS.map(([Icon, label], i) => (
            <li
              key={label}
              className={`flex flex-col items-center gap-3 px-3 py-4 text-center ${
                i % 2 ? 'border-l' : ''
              } ${i > 0 ? 'wide-md:border-l' : ''}`}
              style={{ borderColor: faint }}
            >
              <Icon aria-hidden="true" strokeWidth={1.1} className="h-10 w-10" style={{ color: gold }} />
              <span className="max-w-[10rem] text-[0.72rem] font-medium uppercase leading-relaxed tracking-[0.2em]">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {landing.about?.trim() && (
        <section className="mx-auto max-w-3xl px-5 py-16 text-center sm:py-20">
          <Eyebrow>Welcome</Eyebrow>
          {/* The hero circle's rings, smaller. */}
          {welcomePhoto && (
            <div className="relative mx-auto mb-4 mt-10 aspect-square w-56 sm:w-64">
              <div className="absolute -inset-2.5 rounded-full" style={{ border: `1px solid ${faint}` }} />
              <div className="absolute inset-0 overflow-hidden rounded-full" style={{ boxShadow: `0 0 0 2px ${gold}` }}>
                <Photo photo={{ ...welcomePhoto, ...WELCOME_PHOTO_FOCUS, crop: null }} className="h-full w-full" />
              </div>
            </div>
          )}
          <p
            className="mt-8 whitespace-pre-line text-xl leading-relaxed opacity-90 sm:text-2xl"
            style={{ fontFamily: SERIF }}
          >
            {landing.about}
          </p>
          <p className="mt-6 text-5xl" style={{ fontFamily: SCRIPT, color: gold }}>
            {site.business.name}
          </p>
        </section>
      )}

      <section id="where" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-16 sm:py-20">
        <Eyebrow>Where I groom</Eyebrow>
        <h2 className="mt-5 text-center text-4xl font-medium sm:text-5xl" style={{ fontFamily: SERIF }}>
          Two locations, one groomer
        </h2>
        <ul className="mt-12 grid gap-5 md:grid-cols-3">
          {PLACES.map(([town, kind, detail]) => (
            <li key={town} className="px-6 py-8 text-center" style={{ backgroundColor: panel, border: `1px solid ${faint}` }}>
              <MapPin aria-hidden="true" strokeWidth={1.3} className="mx-auto h-6 w-6" style={{ color: gold }} />
              <p className="mt-4 text-3xl" style={{ fontFamily: SERIF }}>{town}</p>
              <p className="mt-1 text-[0.68rem] font-semibold uppercase tracking-[0.22em]" style={{ color: gold }}>{kind}</p>
              <p className="mt-4 text-sm leading-relaxed opacity-80">{detail}</p>
            </li>
          ))}
        </ul>
      </section>

      {site.services.length > 0 && (
        <section id="services" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-16 sm:py-20">
          <Eyebrow>Services</Eyebrow>
          <h2 className="mt-5 text-center text-4xl font-medium sm:text-5xl" style={{ fontFamily: SERIF }}>
            The grooming menu
          </h2>
          {site.settings.pricingNote && (
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm opacity-70">{site.settings.pricingNote}</p>
          )}
          <div
            className={`mt-12 grid gap-5 ${
              site.services.length === 1 ? 'mx-auto max-w-md' : site.services.length === 2 ? 'mx-auto max-w-3xl sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
            }`}
          >
            {site.services.map((s) => {
              const from = fromCents(s);
              return (
                /* Card click is a convenience; "Book this" is the real, focusable control — see
                   the note on Fluffy Paw's cards for why the card itself is not a <button>. */
                <div
                  key={s.id}
                  onClick={() => onPickService?.(s.id)}
                  className="group relative flex cursor-pointer flex-col items-center gap-3 px-6 pb-7 pt-9 text-center transition-transform hover:-translate-y-1 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                  style={{ backgroundColor: panel, border: `1px solid ${faint}` }}
                >
                  <span className="absolute inset-x-8 top-0 h-px" style={{ backgroundImage: metal }} />
                  <h3 className="text-2xl font-medium leading-tight" style={{ fontFamily: SERIF }}>
                    {s.name}
                  </h3>
                  {s.description && <p className="flex-1 text-sm leading-relaxed opacity-70">{s.description}</p>}
                  <p className="text-[0.68rem] font-medium uppercase tracking-[0.2em] opacity-55">
                    {duration(s.durationMinutes)}
                    {s.tiered && s.tiers.length > 0 && ` · ${s.tiers.length} sizes`}
                  </p>
                  {/* A quote service shows no figure at all — never a zero. */}
                  {from != null && (
                    <p className="text-3xl" style={{ fontFamily: SERIF, color: gold }}>
                      {s.priceIsEstimate || s.tiered ? (
                        <>
                          <span className="mr-1.5 align-middle text-xs uppercase tracking-[0.2em] opacity-70" style={{ fontFamily: SANS }}>
                            From
                          </span>
                          {formatCents(from)}
                        </>
                      ) : (
                        formatCents(from)
                      )}
                    </p>
                  )}
                  {sizeRange(s) && (
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] opacity-50">{sizeRange(s)}</p>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // the card's click would register the pick twice
                      onPickService?.(s.id);
                    }}
                    className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.18em] transition-colors"
                    style={{ border: `1px solid ${gold}`, color: gold }}
                  >
                    <CalendarCheck className="h-3.5 w-3.5" />
                    Book this
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Everything after the hero photo. Three or more get the crawling brick gallery; one or
          two would loop visibly, so they stay a plain row. */}
      {more.length > 0 && (
        <section id="gallery" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-16 sm:py-20">
          <Eyebrow>Gallery</Eyebrow>
          <h2 className="mt-5 text-center text-4xl font-medium sm:text-5xl" style={{ fontFamily: SERIF }}>
            Ready for a close-up
          </h2>
          <div className="mt-12">
            {more.length >= 3 ? (
              <BrickGallery photos={more} accent={gold} border={faint} />
            ) : (
              <div className={`grid gap-4 ${more.length === 1 ? 'mx-auto max-w-md' : 'grid-cols-2'}`}>
                {more.map((p) => (
                  <Photo key={p.id} photo={p} className="h-56 w-full sm:h-80" />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {addons.length > 0 && (
        <section id="extras" className="mx-auto max-w-4xl scroll-mt-16 px-5 py-16 sm:py-20">
          <Eyebrow>Add-ons</Eyebrow>
          <h2 className="mt-5 text-center text-4xl font-medium sm:text-5xl" style={{ fontFamily: SERIF }}>
            A little something extra
          </h2>
          {/* Name left, price right. Dotted leaders were tried and taken out (Mike, 2026-10-26). */}
          <ul className="mt-10 grid gap-x-14 sm:grid-cols-2">
            {addons.map((a) => (
              <li key={a.id} className="flex items-baseline gap-3 py-3">
                <span>
                  <span className="text-lg" style={{ fontFamily: SERIF }}>{a.name}</span>
                  {a.description && <span className="block text-xs opacity-55">{a.description}</span>}
                </span>
                <span className="flex-1" />
                <span className="whitespace-nowrap tabular-nums" style={{ color: gold }}>{addonPrice(a)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {repeats.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 py-6">
          <div className="px-6 py-10 text-center" style={{ border: `1px solid ${faint}`, backgroundColor: panel }}>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.32em]" style={{ color: gold }}>Regulars</p>
            <h2 className="mt-3 text-3xl font-medium sm:text-4xl" style={{ fontFamily: SERIF }}>
              Book on a schedule, save every time.
            </h2>
            <div className="mt-7 flex flex-wrap justify-center gap-x-14 gap-y-5">
              {repeats.map((r) => (
                <div key={r.recurrence}>
                  <div className="text-4xl" style={{ fontFamily: SERIF, color: gold }}>{r.percentOff}% off</div>
                  <div className="mt-1 text-[0.68rem] font-medium uppercase tracking-[0.2em] opacity-65">
                    {everyN[r.recurrence] ?? r.recurrence}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {landing.reviews.length > 0 && (
        <section id="reviews" className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
          <Eyebrow>Kind words</Eyebrow>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {landing.reviews.map((r) => (
              <figure key={r.id} className="px-6 py-7 text-center" style={{ backgroundColor: panel, border: `1px solid ${faint}` }}>
                {/* A star only where she recorded one — never a defaulted five. */}
                {r.rating != null && (
                  <div className="flex justify-center gap-1" style={{ color: gold }}>
                    {Array.from({ length: r.rating }, (_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-current" />
                    ))}
                  </div>
                )}
                <blockquote className="mt-3 text-lg leading-relaxed" style={{ fontFamily: SERIF }}>
                  “{r.quote}”
                </blockquote>
                {(r.author || r.source) && (
                  <figcaption className="mt-4 text-[0.68rem] font-medium uppercase tracking-[0.2em] opacity-60">
                    {[r.author, r.source].filter(Boolean).join(' · ')}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          {landing.reviewUrl && (
            <div className="mt-8 text-center">
              <a
                href={landing.reviewUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] underline underline-offset-4"
                style={{ color: gold }}
              >
                <Star className="h-3.5 w-3.5 fill-current" />
                Leave a review
              </a>
            </div>
          )}
        </section>
      )}

      {/*
        Where and how to reach her — the card's bottom-right corner. The towns are hers (see
        PLACES); there is no address field to read them from.
      */}
      <section className="mx-auto max-w-6xl px-5 py-12">
        <div className="flex flex-col items-center gap-6 text-sm sm:flex-row sm:justify-center sm:gap-12">
          <span className="inline-flex items-center gap-2.5">
            <MapPin aria-hidden="true" strokeWidth={1.4} className="h-5 w-5" style={{ color: gold }} />
            Pendleton &amp; Starr, SC
          </span>
          {facebook && (
            <a
              href={facebook.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2.5 underline-offset-4 hover:underline"
            >
              <MessageCircle aria-hidden="true" strokeWidth={1.4} className="h-5 w-5" style={{ color: gold }} />
              Message me on Facebook
            </a>
          )}
        </div>
        <div className="flex justify-center [&>div]:mt-6">
          <Social links={landing.social.filter((l) => l.platform !== 'facebook')} centered />
        </div>
      </section>
    </div>
  );
}
